import { Recommendation, ActionPlan } from "../types";
import { ACTION_PLAN_TYPE_TEAM_PROJECTS } from "../constants";
import { findCategoryDelegate } from "./actionPlanHelpers";
import { TypeOf3B0Enum } from "@/store/generated/portal/assessments";

interface ActionPlanSaverParams {
  selectedAssessmentPeriodId: number | null;
  organizationId: number | null;
  currentUserId: number | null;
  currentUserDebriefSchedule: { id: number; name_uid?: string } | null;
  categoryDelegates: unknown[] | null;
  existingActionPlans: ActionPlan[] | undefined;
  actionPlanType?: number;
  teamId?: number;
}

interface DeterminedLeaderAndCategory {
  leaderId: number | null;
  categoryUid: string | undefined;
}

/**
 * Determine the leader ID and category UID for action plan operations.
 * This handles category delegate lookups.
 */
export function determineLeaderAndCategory(
  params: ActionPlanSaverParams
): DeterminedLeaderAndCategory {
  const categoryUid = params.currentUserDebriefSchedule?.name_uid
    ? String(params.currentUserDebriefSchedule.name_uid)
    : undefined;

  let leaderId = params.currentUserId;
  if (categoryUid && params.categoryDelegates && Array.isArray(params.categoryDelegates) && params.organizationId) {
    const delegateId = findCategoryDelegate(categoryUid, params.organizationId, params.categoryDelegates as any);
    if (delegateId) {
      leaderId = delegateId;
    }
  }

  return { leaderId, categoryUid };
}



/**
 * Build individual organizationAssessmentPeriodActionPlan objects for each task.
 * This is used when we want to create one action plan record per recommendation/task.
 */
export function buildIndividualActionPlanPayloads(
  tasks: Recommendation[],
  params: ActionPlanSaverParams,
  leaderId: number | null,
  categoryUid: string | undefined
) {
  return tasks.map((task) => {
    const taskIndex: number = (task.task_index as number | undefined) ?? 0;

    let parsedRecommendationId: number | null = null;
    if (typeof task.recommendation_template_id === 'number' && task.recommendation_template_id > 0) {
      parsedRecommendationId = task.recommendation_template_id;
    }

    // Preserve the full task/recommendation payload in recommendation_json to avoid losing fields
    const recommendationJson = {
      ...(task as any),
      task_index: taskIndex,
      recommendation_template_id: parsedRecommendationId,
      // ensure both potential description fields are available for compatibility
      description: task.task_description ?? task.description,
    };

    return {
      // We assert the assessment period id is present (caller ensures this) so it matches the required type
      assessment_period_id: params.selectedAssessmentPeriodId as number,
      // Use undefined instead of null when organization isn't provided so it matches the generated types
      organization: params.organizationId ?? undefined,
      type_of: (params.actionPlanType ?? ACTION_PLAN_TYPE_TEAM_PROJECTS) as TypeOf3B0Enum,
      team_id: params.teamId,
      leader: leaderId,
      category_uid: categoryUid,
      recommendation: parsedRecommendationId,
      recommendation_name: task.task_title || task.task || task.title || 'Untitled',
      recommendation_json: recommendationJson,
      notes: task.notes || null,
    };
  });
}
